import axios from 'axios';
import { createAsyncThunk, isFulfilled, isPending, isRejected } from '@reduxjs/toolkit';

import { cleanEntity } from 'app/shared/util/entity-utils';
import { IQueryParams, createEntitySlice, EntityState, serializeAxiosError } from 'app/shared/reducers/reducer.utils';
import { IVirtualFenceCircleAlert, defaultValue } from 'app/shared/model/virtual-fence-circle-alert.model';
import { IInfoRoutesByCompany, IInfoRoutesByTruckImplement } from 'app/modules/dashboards/dashboard-truck-implement/interface/dashboardTruckImplementInterface';

const initialState: EntityState<IVirtualFenceCircleAlert> = {
  loading: false,
  errorMessage: null,
  entities: [],
  entity: defaultValue,
  updating: false,
  totalItems: 0,
  updateSuccess: false,
};

const apiUrl = 'api/virtual-fence-circle-alerts';

// Actions

export const getEntities = createAsyncThunk('virtualFenceCircleAlert/fetch_entity_list', async ({ page, size, sort }: IQueryParams) => {
  const requestUrl = `${apiUrl}${sort ? `?page=${page}&size=${size}&sort=${sort}&` : '?'}cacheBuster=${new Date().getTime()}`;
  return axios.get<IVirtualFenceCircleAlert[]>(requestUrl);
});

export const getEntity = createAsyncThunk(
  'virtualFenceCircleAlert/fetch_entity',
  async (id: string | number) => {
    const requestUrl = `${apiUrl}/${id}`;
    return axios.get<IVirtualFenceCircleAlert>(requestUrl);
  },
  { serializeError: serializeAxiosError }
);

export const createEntity = createAsyncThunk(
  'virtualFenceCircleAlert/create_entity',
  async (entity: IVirtualFenceCircleAlert, thunkAPI) => {
    const result = await axios.post<IVirtualFenceCircleAlert>(apiUrl, cleanEntity(entity));
    thunkAPI.dispatch(getEntities({}));
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const updateEntity = createAsyncThunk(
  'virtualFenceCircleAlert/update_entity',
  async (entity: IVirtualFenceCircleAlert, thunkAPI) => {
    const result = await axios.put<IVirtualFenceCircleAlert>(`${apiUrl}/${entity.id}`, cleanEntity(entity));
    thunkAPI.dispatch(getEntities({}));
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const partialUpdateEntity = createAsyncThunk(
  'virtualFenceCircleAlert/partial_update_entity',
  async (entity: IVirtualFenceCircleAlert, thunkAPI) => {
    const result = await axios.patch<IVirtualFenceCircleAlert>(`${apiUrl}/${entity.id}`, cleanEntity(entity));
    thunkAPI.dispatch(getEntities({}));
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const deleteEntity = createAsyncThunk(
  'virtualFenceCircleAlert/delete_entity',
  async (id: string | number, thunkAPI) => {
    const requestUrl = `${apiUrl}/${id}`;
    const result = await axios.delete<IVirtualFenceCircleAlert>(requestUrl);
    thunkAPI.dispatch(getEntities({}));
    return result;
  },
  { serializeError: serializeAxiosError }
);

interface IAllVirtualFenceAlertsByTruckImplementId extends IQueryParams {
  truckImplementId: string | number;
}
export const getAllVirtualFenceAlertsByTruckImplementId = createAsyncThunk(
  'virtualFenceCircleAlert/fetch_virtual_fence_alerts_by_truck_implement_id',
  async ({ page, size, sort, truckImplementId }: IAllVirtualFenceAlertsByTruckImplementId) => {
    const requestUrl = `${apiUrl}/truck-implement/${truckImplementId}${
      sort ? `?page=${page}&size=${size}&sort=${sort}&` : '?'
    }cacheBuster=${new Date().getTime()}`;
    return axios.get<IVirtualFenceCircleAlert[]>(requestUrl);
  }
);

export const getAllVirtualFenceAlertsByTruckImplement = createAsyncThunk(
  'virtualFenceCircleAlert/fetch_infractions_by_truck_implement',
  async ({ page, size, sort, truckImplementId, dateStartPeriodic, dateEndPeriodic, nameSearch, virtualFenceId }: IInfoRoutesByTruckImplement) => {
    const fenceParam = virtualFenceId != null ? `&virtualFenceId=${virtualFenceId}` : '';
    return axios.get<IVirtualFenceCircleAlert[]>(
      `${apiUrl}${`/truck-implement/${truckImplementId}/${dateStartPeriodic}/${dateEndPeriodic}?page=${page}&size=${size}&sort=${sort}&nameSearch=${nameSearch}${fenceParam}&cacheBuster=${new Date().getTime()}`}`
    );
  },
  { serializeError: serializeAxiosError }
);

export const getAllVirtualFenceAlertsByTruckImplementAndPeriodic = createAsyncThunk(
  'virtualFenceCircleAlert/fetch_infractions_by_truck_implement_and_periodic',
  async ({ page, size, sort, truckImplementId, periodicType, nameSearch, virtualFenceId }: IInfoRoutesByTruckImplement) => {
    const fenceParam = virtualFenceId != null ? `&virtualFenceId=${virtualFenceId}` : '';
    return axios.get<IVirtualFenceCircleAlert[]>(
      `${apiUrl}${`/truck-implement/${truckImplementId}/${periodicType}?page=${page}&size=${size}&sort=${sort}&nameSearch=${nameSearch}${fenceParam}&cacheBuster=${new Date().getTime()}`}`
    );
  },
  { serializeError: serializeAxiosError }
);

export const getVirtualFenceAlertsActivationByCompanyAndDateStartAndDateEnd = createAsyncThunk(
  'virtualFenceCircleAlert/fetch_infractions_by_company_and_date_start_and_date_end',
  async ({ page, size, sort, dateStartPeriodic, dateEndPeriodic, nameSearch, licensePlateSearch, searchCompanyId, truckImplementId }: IInfoRoutesByCompany) => {
    return axios.get<IVirtualFenceCircleAlert[]>(
      `${apiUrl}${`/company/${dateStartPeriodic}/${dateEndPeriodic}?page=${page}&size=${size}&sort=${sort}&nameSearch=${nameSearch}&licensePlateSearch=${licensePlateSearch}&cacheBuster=${new Date().getTime()}`}`,
      { params: { searchCompanyId, truckImplementId } }
    );
  },
  { serializeError: serializeAxiosError }
);

export const getVirtualFenceAlertsActivationByCompanyAndPeriodic = createAsyncThunk(
  'virtualFenceCircleAlert/fetch_infractions_by_company_and_periodic',
  async ({ page, size, sort, periodicType, nameSearch, licensePlateSearch, searchCompanyId, truckImplementId }: IInfoRoutesByCompany) => {
    return axios.get<IVirtualFenceCircleAlert[]>(
      `${apiUrl}${`/company/${periodicType}?page=${page}&size=${size}&sort=${sort}&nameSearch=${nameSearch}&licensePlateSearch=${licensePlateSearch}&cacheBuster=${new Date().getTime()}`}`,
      { params: { searchCompanyId, truckImplementId } }
    );
  },
  { serializeError: serializeAxiosError }
);

// slice

export const VirtualFenceCircleAlertSlice = createEntitySlice({
  name: 'virtualFenceCircleAlert',
  initialState,
  extraReducers(builder) {
    builder
      .addCase(getEntity.fulfilled, (state, action) => {
        state.loading = false;
        state.entity = action.payload.data;
      })
      .addCase(deleteEntity.fulfilled, state => {
        state.updating = false;
        state.updateSuccess = true;
        state.entity = {};
      })
      .addMatcher(isFulfilled(getAllVirtualFenceAlertsByTruckImplementId), (state, action) => {
        return {
          ...state,
          loading: false,
          entities: action.payload.data,
          totalItems: parseInt(action.payload.headers['x-total-count'], 10),
        };
      })
      .addMatcher(isFulfilled(getAllVirtualFenceAlertsByTruckImplement), (state, action) => {
        return {
          ...state,
          loading: false,
          entities: action.payload.data,
          totalItems: parseInt(action.payload.headers['x-total-count'], 10),
        };
      })
      .addMatcher(isFulfilled(getAllVirtualFenceAlertsByTruckImplementAndPeriodic), (state, action) => {
        return {
          ...state,
          loading: false,
          entities: action.payload.data,
          totalItems: parseInt(action.payload.headers['x-total-count'], 10),
        };
      })
      .addMatcher(isFulfilled(getVirtualFenceAlertsActivationByCompanyAndDateStartAndDateEnd), (state, action) => {
        return {
          ...state,
          loading: false,
          entities: action.payload.data,
          totalItems: parseInt(action.payload.headers['x-total-count'], 10),
        };
      })
      .addMatcher(isFulfilled(getVirtualFenceAlertsActivationByCompanyAndPeriodic), (state, action) => {
        return {
          ...state,
          loading: false,
          entities: action.payload.data,
          totalItems: parseInt(action.payload.headers['x-total-count'], 10),
        };
      })
      .addMatcher(isFulfilled(getEntities), (state, action) => {
        return {
          ...state,
          loading: false,
          entities: action.payload.data,
          totalItems: parseInt(action.payload.headers['x-total-count'], 10),
        };
      })
      .addMatcher(isFulfilled(createEntity, updateEntity, partialUpdateEntity), (state, action) => {
        state.updating = false;
        state.loading = false;
        state.updateSuccess = true;
        state.entity = action.payload.data;
      })
      .addMatcher(
        isPending(
          getEntities,
          getEntity,
          getAllVirtualFenceAlertsByTruckImplementId,
          getAllVirtualFenceAlertsByTruckImplement,
          getAllVirtualFenceAlertsByTruckImplementAndPeriodic,
          getVirtualFenceAlertsActivationByCompanyAndDateStartAndDateEnd,
          getVirtualFenceAlertsActivationByCompanyAndPeriodic
        ),
        state => {
          state.errorMessage = null;
          state.updateSuccess = false;
          state.loading = true;
        }
      )
      .addMatcher(isPending(createEntity, updateEntity, partialUpdateEntity, deleteEntity), state => {
        state.errorMessage = null;
        state.updateSuccess = false;
        state.updating = true;
      });
  },
});

export const { reset } = VirtualFenceCircleAlertSlice.actions;

// Reducer
export default VirtualFenceCircleAlertSlice.reducer;
